import React, { useState, useEffect } from 'react';
import './App.css';
import { useWebHaptics } from "web-haptics/react";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Instructions from './components/Instructions';
import GuessInput from './components/GuessInput';
import GameBoard from './components/GameBoard';
import GameOver from './components/GameOver';
import ElementChart from './components/ElementChart';
import Message from './components/Message';
import AuthModal from './components/AuthModal';
import StatsModal from './components/StatsModal';
import HowToPlayModal from './components/HowToPlayModal';
import { api } from './services/api';
import { saveGameState, loadGameState, getLocalDate, clearGameState, hasSeenTutorial, markTutorialAsSeen } from './utils/storage';
import { MAX_GUESSES } from './utils/constants';

function AppContent() {
  const { trigger } = useWebHaptics();
  const { isAuthenticated, login, register, syncGameProgress, migrateLocalData, loadGameProgress, checkGameCompletion } = useAuth();
  const [skylanders, setSkylanders] = useState([]);
  const [guesses, setGuesses] = useState([]);
  const [guessResults, setGuessResults] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [gameOverModalOpen, setGameOverModalOpen] = useState(false);
  const [howToPlayModalOpen, setHowToPlayModalOpen] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState(null);

  // Load game state from backend or localStorage
  useEffect(() => {
    const initGame = async () => {
      try {
        // Fetch skylander names from backend
        const names = await api.getSkylanderNames();
        setSkylanders(names);

        // Try to load from backend if authenticated
        if (isAuthenticated) {
          const backendState = await loadGameProgress(getLocalDate());
          if (backendState && backendState.found) {
            // Restore guesses from backend
            const results = [];
            for (const guess of backendState.guesses) {
              try {
                const result = await api.submitGuess(guess, getLocalDate());
                results.push(result);
              } catch (error) {
                console.error('Error restoring guess:', error);
              }
            }
            setGuesses(backendState.guesses);
            setGuessResults(results);
            const isGameOver = backendState.won || backendState.guesses.length >= MAX_GUESSES;
            setGameOver(isGameOver);
            setWon(backendState.won);
            
            // Fetch correct answer if game was lost
            if (isGameOver && !backendState.won && backendState.guesses.length >= MAX_GUESSES) {
              try {
                const answerData = await api.revealAnswer(getLocalDate(), backendState.guesses);
                setCorrectAnswer(answerData.skylander);
              } catch (error) {
                console.error('Error fetching correct answer on load:', error);
              }
            }
          }
        } else {
          // Load from localStorage for guests
          const savedState = loadGameState();
          if (savedState) {
            // Restore guesses by re-submitting them to backend
            const results = [];
            for (const guess of savedState.guesses) {
              try {
                const result = await api.submitGuess(guess, getLocalDate());
                results.push(result);
              } catch (error) {
                console.error('Error restoring guess:', error);
              }
            }
            setGuesses(savedState.guesses);
            setGuessResults(results);
            setGameOver(savedState.gameOver);
            
            // Check if won
            const isWon = results.length > 0 && results[results.length - 1].is_correct;
            if (isWon) {
              setWon(true);
            }
            
            // Fetch correct answer if game was lost
            if (savedState.gameOver && !isWon && savedState.guesses.length >= MAX_GUESSES) {
              try {
                const answerData = await api.revealAnswer(getLocalDate(), savedState.guesses);
                setCorrectAnswer(answerData.skylander);
              } catch (error) {
                console.error('Error fetching correct answer on load:', error);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize game:', error);
        showMessage('Failed to load game data');
      } finally {
        setLoading(false);
      }
    };

    initGame();
  }, [isAuthenticated, loadGameProgress]); // loadGameProgress is now memoized in AuthContext

  // Auto-show How to Play modal for first-time guest users
  useEffect(() => {
    if (!loading && !isAuthenticated && !hasSeenTutorial()) {
      const savedState = loadGameState();
      // Only show if there's no saved game state (truly first-time user)
      if (!savedState || savedState.guesses.length === 0) {
        setHowToPlayModalOpen(true);
        markTutorialAsSeen();
      }
    }
  }, [loading, isAuthenticated]);

  // Automatically migrate localStorage data when user authenticates
  useEffect(() => {
    const autoMigrate = async () => {
      if (isAuthenticated) {
        const savedState = loadGameState();
        if (savedState && savedState.guesses.length > 0) {
          // Check if user already has a game for today (completed or in-progress)
          const completion = await checkGameCompletion(getLocalDate());
          
          if (completion && completion.exists) {
            // User already has a game for today, just clear localStorage
            clearGameState();
            // Reload the page to show their existing game from backend
            window.location.reload();
          } else {
            // User hasn't completed today's game, migrate their guest progress
            try {
              // Determine if the guest game was won by checking the last guess
              let isWon = false;
              if (savedState.gameOver && savedState.guesses.length > 0) {
                const lastGuess = savedState.guesses[savedState.guesses.length - 1];
                try {
                  const lastResult = await api.submitGuess(lastGuess, getLocalDate());
                  isWon = lastResult.is_correct;
                } catch (error) {
                  console.error('Error checking last guess:', error);
                }
              }
              
              const gameHistory = [{
                date: getLocalDate(),
                guesses: savedState.guesses,
                won: isWon,
                guess_count: savedState.guesses.length
              }];
              
              await migrateLocalData(gameHistory);
              clearGameState();
              // Reload to show migrated game
              window.location.reload();
            } catch (error) {
              console.error('Auto-migration failed:', error);
            }
          }
        }
      }
    };

    autoMigrate();
  }, [isAuthenticated, checkGameCompletion, migrateLocalData]);

  // Automatically show GameOver modal when game ends
  useEffect(() => {
    if (gameOver) {
      // Wait for all tile animations to complete before showing modal
      // 7 tiles × 500ms stagger + 500ms flip duration + 200ms buffer = 3700ms
      setTimeout(() => {
        setGameOverModalOpen(true);
      }, 3700);
    }
  }, [gameOver]);

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 2000);
  };

  const handleGuessSubmit = async (guess) => {
    const guessUpper = guess.toUpperCase();

    // Validate guess
    if (!skylanders.includes(guessUpper)) {
      showMessage('Check Skylander name!');
      return;
    }

    if (guesses.includes(guessUpper)) {
      showMessage('Already guessed!');
      return;
    }

    try {
      // Submit guess to backend
      const result = await api.submitGuess(guessUpper, getLocalDate());
      
      // Trigger haptic feedback for guess submission
      trigger([
        { duration: 30 },
        { delay: 60, duration: 40, intensity: 1 },
      ]);
      
      // Update state
      const newGuesses = [...guesses, guessUpper];
      const newGuessResults = [...guessResults, result];
      
      setGuesses(newGuesses);
      setGuessResults(newGuessResults);

      const isGameWon = result.is_correct;
      const isGameOver = isGameWon || newGuesses.length >= MAX_GUESSES;

      // Save to localStorage (for guests)
      saveGameState(newGuesses, isGameOver);

      // Sync to backend if authenticated
      if (isAuthenticated && isGameOver) {
        await syncGameProgress(getLocalDate(), newGuesses, isGameWon, newGuesses.length);
      }

      // Check if won or lost
      if (isGameWon) {
        setGameOver(true);
        setWon(true);
      } else if (newGuesses.length >= MAX_GUESSES) {
        setGameOver(true);
        setWon(false);
        // Fetch the correct answer when the game is lost
        try {
          const answerData = await api.revealAnswer(getLocalDate(), newGuesses);
          setCorrectAnswer(answerData.skylander);
        } catch (error) {
          console.error('Error fetching correct answer:', error);
        }
      }
    } catch (error) {
      console.error('Error submitting guess:', error);
      showMessage(error.message || 'Failed to submit guess');
    }
  };

  const handleAuthSuccess = async (email, password, isLogin) => {
    const result = isLogin ? await login(email, password) : await register(email, password);
    
    if (result.success) {
      showMessage(isLogin ? 'Logged in successfully!' : 'Account created!');
      return result;
    }
    return result;
  };

  const handleShare = () => {
    const text = generateShareText();
    navigator.clipboard.writeText(text)
      .then(() => showMessage('Score copied!'))
      .catch(() => showMessage('Failed to copy'));
  };

  const generateShareText = () => {
    // Get current date in mm/dd/yy format
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const dateStr = `${month}/${day}/${year}`;
    
    let text = `🎮 Skylandle Daily 🎮\n`;
    text += `${dateStr}\n`;
    text += `${won ? guesses.length : 'X'}/${MAX_GUESSES} ${won ? '🏆' : '💫'}\n\n`;
    
    guessResults.forEach((result) => {
      const row = [
        result.results.game.status === 'correct' ? '🟩' : '🟥',
        result.results.element.status === 'correct' ? '🟩' : 
          (result.results.element.status === 'partial' ? '🟨' : '🟥'),
        result.results.speed.status === 'correct' ? '🟩' : '🟥',
        result.results.health.status === 'correct' ? '🟩' : '🟥',
        result.results.armor.status === 'correct' ? '🟩' : '🟥',
        result.results.gender.status === 'correct' ? '🟩' : '🟥',
        result.results.color.status === 'correct' ? '🟩' : '🟥'
      ].join('');
      text += row + '\n';
    });
    text += "https://skylandle.net\n";
    
    return text;
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header 
        onOpenStats={() => setStatsModalOpen(true)}
        onOpenAuth={() => setAuthModalOpen(true)}
        onOpenHelp={() => setHowToPlayModalOpen(true)}
      />
      <Instructions />
      
      <div className="game-info">
        <span>Guesses: {guesses.length}/{MAX_GUESSES}</span>
      </div>

      <GuessInput
        skylanders={skylanders}
        guesses={guesses}
        onSubmit={handleGuessSubmit}
        disabled={gameOver}
      />

      <Message text={message} />

      <GameBoard guessResults={guessResults} />

      {gameOver && (
        <button 
          onClick={() => setGameOverModalOpen(true)} 
          className="see-results-btn"
        >
          📊 See Results
        </button>
      )}

      {gameOverModalOpen && (
        <GameOver
          won={won}
          guessResults={guessResults}
          correctAnswer={correctAnswer}
          onShare={handleShare}
          onClose={() => setGameOverModalOpen(false)}
          onOpenAuth={() => {
            setGameOverModalOpen(false);
            setAuthModalOpen(true);
          }}
        />
      )}

      <ElementChart />

      <AuthModal 
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      <StatsModal 
        isOpen={statsModalOpen}
        onClose={() => setStatsModalOpen(false)}
        currentGuessCount={won ? guesses.length : null}
      />

      <HowToPlayModal 
        isOpen={howToPlayModalOpen}
        onClose={() => setHowToPlayModalOpen(false)}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
